/** Hunter — normal balls plus red hunter balls that patrol zones and hunt in coordinated packs. */

import { gameRandom } from "./game-rng";

export const HUNTER_NORMAL_START = 24;
export const HUNTER_NORMAL_PER_SPAWN = 10;
export const HUNTER_RED_PER_SPAWN = 2;
export const HUNTER_NORMAL_SPEED = 0.8;
export const HUNTER_SCORE_PER_SPAWN = 250;
export const HUNTER_CHASE_SPEED = 1.08;
export const HUNTER_SPAWN_CLEARANCE_REF_PX = 160;
export const HUNTER_RING_RADIUS_REF_PX = 72;
export const HUNTER_SEPARATION_REF_PX = 48;
export const HUNTER_PATROL_RADIUS_REF_PX = 56;
export const HUNTER_TURN_RATE = 0.16;
export const HUNTER_PREDICT_LEAD = 16;

let chaseSpeedOverride: number | null = null;
let patrolRadiusOverride: number | null = null;
let turnRateOverride: number | null = null;

export function setHunterTuning(opts?: {
    chaseSpeed?: number;
    patrolRadius?: number;
    turnRate?: number;
}): void {
    chaseSpeedOverride =
        opts?.chaseSpeed != null && Number.isFinite(opts.chaseSpeed) ? Math.max(0.05, opts.chaseSpeed) : null;
    patrolRadiusOverride =
        opts?.patrolRadius != null && Number.isFinite(opts.patrolRadius)
            ? Math.max(1, opts.patrolRadius)
            : null;
    turnRateOverride =
        opts?.turnRate != null && Number.isFinite(opts.turnRate) ? Math.max(0.01, opts.turnRate) : null;
}

export const HUNTER_BALL_FILL = "#ff1a1a";
export const HUNTER_BALL_GLOW = "#ff5555";

export type HunterBallLike = {
    x: number;
    y: number;
    vx: number;
    vy: number;
    r: number;
    lastBounceFx: number;
    isHunter?: boolean;
    /** Stable slot bias (radians) assigned at spawn for approach variety. */
    hunterBias?: number;
    /** Patrol anchor assigned at spawn; hunters idle near this point until the cursor enters the zone. */
    hunterPatrolX?: number;
    hunterPatrolY?: number;
    /** Whether this hunter is actively hunting (chase / block / flank). */
    hunterActive?: boolean;
};

type HunterRoles = { chase: number; block: number; flank: number };

export function hunterSpawnClearancePx(
    layoutScale: number,
    ballRadiusPx: number,
    cursorRadiusPx: number,
): number {
    return cursorRadiusPx + ballRadiusPx + HUNTER_SPAWN_CLEARANCE_REF_PX * layoutScale;
}

/** Place a ball at least `minDist` px from (cx, cy). Always prefers farthest legal point over random-inside. */
export function placeBallAwayFromCursor(
    ball: HunterBallLike,
    cx: number,
    cy: number,
    minDist: number,
    canvasW: number,
    canvasH: number,
): boolean {
    const need = Math.max(0, minDist);
    const loX = 0;
    const hiX = Math.max(0, canvasW);
    const loY = 0;
    const hiY = Math.max(0, canvasH);
    const spanX = hiX - loX;
    const spanY = hiY - loY;

    // Fixed RNG consumption (cursor-dependent early-exit desyncs replays).
    let foundX = 0;
    let foundY = 0;
    let found = false;
    for (let attempt = 0; attempt < 200; attempt++) {
        const nx = gameRandom() * canvasW;
        const ny = gameRandom() * canvasH;
        if (!found && Math.hypot(nx - cx, ny - cy) >= need) {
            foundX = nx;
            foundY = ny;
            found = true;
        }
    }
    if (found) {
        ball.x = foundX;
        ball.y = foundY;
        return true;
    }

    const candidates: Array<[number, number]> = [
        [loX, loY],
        [hiX, loY],
        [loX, hiY],
        [hiX, hiY],
    ];
    const edgeN = 32;
    for (let i = 0; i <= edgeN; i++) {
        const t = i / edgeN;
        candidates.push([loX + t * spanX, loY], [loX + t * spanX, hiY], [loX, loY + t * spanY], [hiX, loY + t * spanY]);
    }
    for (let a = 0; a < 48; a++) {
        const ang = (a / 48) * Math.PI * 2;
        for (const mul of [1, 1.1, 1.25, 1.5, 2]) {
            candidates.push([cx + Math.cos(ang) * need * mul, cy + Math.sin(ang) * need * mul]);
        }
    }

    let bestOk: { x: number; y: number } | null = null;
    let bestOkScore = -1;
    let bestAny = { x: loX, y: loY };
    let bestAnyScore = -1;
    const clamp = (n: number, a: number, b: number) => Math.max(a, Math.min(b, n));
    for (const [rx, ry] of candidates) {
        const x = clamp(rx, loX, hiX);
        const y = clamp(ry, loY, hiY);
        const score = Math.hypot(x - cx, y - cy);
        if (score > bestAnyScore) {
            bestAnyScore = score;
            bestAny = { x, y };
        }
        if (score >= need && score > bestOkScore) {
            bestOkScore = score;
            bestOk = { x, y };
        }
    }
    const pick = bestOk || bestAny;
    ball.x = pick.x;
    ball.y = pick.y;
    return bestOk != null;
}

/** More hunters → more patrol zones spread across the arena. */
function zoneCountForHunters(n: number): number {
    if (n <= 1) return 1;
    return Math.max(2, Math.min(14, Math.ceil(Math.sqrt(n) * 2.6)));
}

function patrolAnchorForSlot(
    slot: number,
    zoneCount: number,
    canvasW: number,
    canvasH: number,
    margin: number,
    bias: number,
): { x: number; y: number } {
    const cols = Math.ceil(Math.sqrt(zoneCount));
    const rows = Math.ceil(zoneCount / cols);
    const col = slot % cols;
    const row = Math.floor(slot / cols) % rows;
    const cellW = Math.max(1, canvasW - margin * 2) / cols;
    const cellH = Math.max(1, canvasH - margin * 2) / rows;
    const jitter = 0.28;
    const jx = Math.sin(bias * 3.7) * 0.5 * cellW * jitter;
    const jy = Math.cos(bias * 2.3) * 0.5 * cellH * jitter;
    return {
        x: margin + (col + 0.5) * cellW + jx,
        y: margin + (row + 0.5) * cellH + jy,
    };
}

/** Assign a map zone anchor so hunters spread out instead of clustering on the player. */
export function assignHunterPatrolAnchor(
    ball: HunterBallLike,
    slotIndex: number,
    totalHunters: number,
    canvasW: number,
    canvasH: number,
    layoutScale: number,
): void {
    const zones = zoneCountForHunters(totalHunters);
    const margin = ball.r * 4 + 28 * layoutScale;
    const bias = ball.hunterBias ?? slotIndex * 1.618;
    const anchor = patrolAnchorForSlot(
        slotIndex % zones,
        zones,
        canvasW,
        canvasH,
        margin,
        bias,
    );
    ball.hunterPatrolX = anchor.x;
    ball.hunterPatrolY = anchor.y;
}

function clamp01(v: number): number {
    return Math.max(0, Math.min(1, v));
}

function clampSpeed(vx: number, vy: number, maxSpeed: number): { vx: number; vy: number } {
    const spd = Math.hypot(vx, vy);
    if (spd <= maxSpeed || spd < 1e-8) return { vx, vy };
    const s = maxSpeed / spd;
    return { vx: vx * s, vy: vy * s };
}

function steerToward(
    ball: HunterBallLike,
    targetX: number,
    targetY: number,
    maxSpeed: number,
    turnRate: number,
): void {
    const dx = targetX - ball.x;
    const dy = targetY - ball.y;
    const dist = Math.hypot(dx, dy);
    let desiredVx = 0;
    let desiredVy = 0;
    if (dist > 1e-6) {
        desiredVx = (dx / dist) * maxSpeed;
        desiredVy = (dy / dist) * maxSpeed;
    }
    ball.vx += (desiredVx - ball.vx) * turnRate;
    ball.vy += (desiredVy - ball.vy) * turnRate;
    const clamped = clampSpeed(ball.vx, ball.vy, maxSpeed);
    ball.vx = clamped.vx;
    ball.vy = clamped.vy;
}

function bounceOffWalls(
    ball: HunterBallLike,
    canvasW: number,
    canvasH: number,
): boolean {
    let bounced = false;
    if (ball.x < ball.r) {
        ball.x = ball.r;
        ball.vx = Math.abs(ball.vx);
        bounced = true;
    } else if (ball.x > canvasW - ball.r) {
        ball.x = canvasW - ball.r;
        ball.vx = -Math.abs(ball.vx);
        bounced = true;
    }
    if (ball.y < ball.r) {
        ball.y = ball.r;
        ball.vy = Math.abs(ball.vy);
        bounced = true;
    } else if (ball.y > canvasH - ball.r) {
        ball.y = canvasH - ball.r;
        ball.vy = -Math.abs(ball.vy);
        bounced = true;
    }
    return bounced;
}

function ensurePatrolAnchor(
    ball: HunterBallLike,
    index: number,
    total: number,
    canvasW: number,
    canvasH: number,
    layoutScale: number,
): { x: number; y: number } {
    if (ball.hunterPatrolX == null || ball.hunterPatrolY == null) {
        assignHunterPatrolAnchor(ball, index, total, canvasW, canvasH, layoutScale);
    }
    return { x: ball.hunterPatrolX!, y: ball.hunterPatrolY! };
}

/** More hunters → tighter ring, stronger separation, fewer direct chasers. */
function hunterRoleCounts(n: number, hasMove: boolean): HunterRoles {
    if (n <= 0) return { chase: 0, block: 0, flank: 0 };
    if (n === 1) return { chase: 1, block: 0, flank: 0 };
    if (n === 2) return hasMove ? { chase: 1, block: 0, flank: 1 } : { chase: 1, block: 0, flank: 1 };

    const coord = clamp01(n / 10);
    const chase = Math.min(2, Math.max(1, Math.round(2 - coord * 1.2)));
    const block = hasMove ? Math.min(Math.max(1, Math.floor(n * (0.28 + coord * 0.12))), n - 1) : 0;
    const chaseClamped = Math.min(chase, n);
    const blockClamped = Math.min(block, Math.max(0, n - chaseClamped));
    return { chase: chaseClamped, block: blockClamped, flank: n - chaseClamped - blockClamped };
}

function roleTarget(
    role: "chase" | "block" | "flank",
    px: number,
    py: number,
    moveAngle: number | null,
    ringR: number,
    slotInRole: number,
    roleCount: number,
    bias: number,
    layoutScale: number,
): { x: number; y: number } {
    const jitter = bias * 10 * layoutScale;
    if (role === "chase") {
        return {
            x: px + Math.cos(bias) * jitter,
            y: py + Math.sin(bias) * jitter,
        };
    }
    if (role === "block" && moveAngle != null) {
        const arc = Math.PI * 0.75;
        const t = roleCount <= 1 ? 0 : (slotInRole / (roleCount - 1) - 0.5) * arc;
        const angle = moveAngle + t;
        const blockR = ringR * (0.75 + slotInRole * 0.08);
        return { x: px + Math.cos(angle) * blockR, y: py + Math.sin(angle) * blockR };
    }
    const spread = (Math.PI * 2) / Math.max(1, roleCount);
    const base = moveAngle ?? 0;
    const angle = base + (slotInRole + 0.5) * spread + bias * 0.18;
    return { x: px + Math.cos(angle) * ringR, y: py + Math.sin(angle) * ringR };
}

function applySeparation(
    tx: number,
    ty: number,
    ball: HunterBallLike,
    others: HunterBallLike[],
    sepR: number,
    ringR: number,
    weight: number,
): { x: number; y: number } {
    let ox = tx;
    let oy = ty;
    for (const other of others) {
        if (other === ball) continue;
        const dx = ball.x - other.x;
        const dy = ball.y - other.y;
        const dist = Math.hypot(dx, dy);
        if (dist >= sepR || dist < 1e-6) continue;
        const push = ((sepR - dist) / sepR) * weight;
        ox += (dx / dist) * push * ringR;
        oy += (dy / dist) * push * ringR;
    }
    return { x: ox, y: oy };
}

function selectActiveHunters(
    hunters: HunterBallLike[],
    cursorX: number,
    cursorY: number,
    activateRadius: number,
    engageRadius: number,
    releaseRadius: number,
    maxActive: number,
): boolean[] {
    const n = hunters.length;
    const active = new Array<boolean>(n).fill(false);
    if (n === 0) return active;

    type Candidate = { index: number; priority: number };
    const candidates: Candidate[] = [];

    for (let i = 0; i < n; i++) {
        const ball = hunters[i]!;
        const anchor = { x: ball.hunterPatrolX ?? ball.x, y: ball.hunterPatrolY ?? ball.y };
        const zoneDist = Math.hypot(cursorX - anchor.x, cursorY - anchor.y);
        const ballDist = Math.hypot(ball.x - cursorX, ball.y - cursorY);
        const zoneTriggered = zoneDist < activateRadius;
        const closeEngage = ballDist < engageRadius;
        const sticky = !!ball.hunterActive && ballDist < releaseRadius;

        let priority = 0;
        if (zoneTriggered) priority += 12000 - zoneDist;
        if (closeEngage) priority += 4000 - ballDist;
        if (sticky) priority += 800;

        if (priority > 0) candidates.push({ index: i, priority });
    }

    candidates.sort((a, b) => b.priority - a.priority);
    const cap = Math.min(maxActive, candidates.length);
    for (let k = 0; k < cap; k++) {
        active[candidates[k]!.index] = true;
    }

    for (let i = 0; i < n; i++) {
        hunters[i]!.hunterActive = active[i];
    }
    return active;
}

function patrolTarget(
    ball: HunterBallLike,
    anchorX: number,
    anchorY: number,
    patrolRadius: number,
): { x: number; y: number } {
    const bias = ball.hunterBias ?? 0;
    const phase = bias + (ball.x + ball.y) * 0.0018 + ball.vx * 0.04 + ball.vy * 0.04;
    const distFromAnchor = Math.hypot(ball.x - anchorX, ball.y - anchorY);
    if (distFromAnchor > patrolRadius * 2.8) {
        return { x: anchorX, y: anchorY };
    }
    return {
        x: anchorX + Math.cos(phase) * patrolRadius,
        y: anchorY + Math.sin(phase * 1.13) * patrolRadius * 0.88,
    };
}

function computePackTargets(
    activeHunters: HunterBallLike[],
    activeIndices: number[],
    cursorX: number,
    cursorY: number,
    cursorVx: number,
    cursorVy: number,
    layoutScale: number,
): Map<number, { x: number; y: number }> {
    const n = activeHunters.length;
    const targets = new Map<number, { x: number; y: number }>();
    if (n === 0) return targets;

    const coord = clamp01(n / 10);
    const ringR = HUNTER_RING_RADIUS_REF_PX * layoutScale * (1 - coord * 0.12);
    const sepR = HUNTER_SEPARATION_REF_PX * layoutScale * (1 + coord * 0.35);
    const sepWeight = 0.42 + coord * 0.38;
    const lead = HUNTER_PREDICT_LEAD * layoutScale * (1 + coord * 0.2);

    const moveSpd = Math.hypot(cursorVx, cursorVy);
    const moveThreshold = 0.35 * layoutScale;
    const hasMove = moveSpd > moveThreshold;
    const predictScale = hasMove ? Math.min(lead, moveSpd * 3.4) : 0;
    const px = cursorX + (hasMove ? (cursorVx / moveSpd) * predictScale : 0);
    const py = cursorY + (hasMove ? (cursorVy / moveSpd) * predictScale : 0);
    const moveAngle = hasMove ? Math.atan2(cursorVy, cursorVx) : null;

    const roles = hunterRoleCounts(n, hasMove);
    const indexed = activeHunters.map((ball, slot) => ({
        ball,
        slot,
        globalIndex: activeIndices[slot]!,
        angleFromCenter: Math.atan2(ball.y - py, ball.x - px),
        bias: ball.hunterBias ?? 0,
    }));
    indexed.sort((a, b) => a.angleFromCenter - b.angleFromCenter);

    let chaseSlot = 0;
    let blockSlot = 0;
    let flankSlot = 0;

    indexed.forEach((entry, slot) => {
        let role: "chase" | "block" | "flank";
        let slotInRole: number;
        if (slot < roles.chase) {
            role = "chase";
            slotInRole = chaseSlot++;
        } else if (slot < roles.chase + roles.block) {
            role = "block";
            slotInRole = blockSlot++;
        } else {
            role = "flank";
            slotInRole = flankSlot++;
        }

        const roleCount =
            role === "chase" ? roles.chase : role === "block" ? roles.block : roles.flank;
        let { x: tx, y: ty } = roleTarget(
            role,
            px,
            py,
            moveAngle,
            ringR,
            slotInRole,
            roleCount,
            entry.bias,
            layoutScale,
        );

        const dist = Math.hypot(entry.ball.x - px, entry.ball.y - py);
        const approachBlend = clamp01((dist - ringR * 1.6) / (ringR * 2.4));
        if (approachBlend > 0) {
            tx = tx * (1 - approachBlend) + px * approachBlend;
            ty = ty * (1 - approachBlend) + py * approachBlend;
        }

        const sep = applySeparation(tx, ty, entry.ball, activeHunters, sepR, ringR, sepWeight);
        targets.set(entry.globalIndex, sep);
    });

    return targets;
}

/** Zone patrol when idle; coordinated pack hunt when the cursor enters a hunter's area. */
export function updateHunterBalls(
    hunters: HunterBallLike[],
    cursorX: number,
    cursorY: number,
    cursorVx: number,
    cursorVy: number,
    mult: number,
    canvasW: number,
    canvasH: number,
    layoutScale: number,
): boolean[] {
    const n = hunters.length;
    const bounced: boolean[] = new Array(n).fill(false);
    if (n === 0) return bounced;

    const maxSpeed = (chaseSpeedOverride ?? HUNTER_CHASE_SPEED) * layoutScale;
    const coord = clamp01(n / 12);
    const turnRate = Math.min(1, (turnRateOverride ?? HUNTER_TURN_RATE) * mult * (1 + coord * 0.25));
    const ringR = HUNTER_RING_RADIUS_REF_PX * layoutScale * (1 - coord * 0.12);
    const minDim = Math.min(canvasW, canvasH);
    const activateRadius = minDim * (0.2 + coord * 0.07);
    const engageRadius = ringR * 2.4;
    const releaseRadius = ringR * 4.8;
    const maxActive = Math.min(n, Math.max(1, Math.ceil(n * (0.26 + coord * 0.12))));
    const patrolSpeed = maxSpeed * (0.56 + coord * 0.05);
    const patrolRadius = (patrolRadiusOverride ?? HUNTER_PATROL_RADIUS_REF_PX) * layoutScale * (1 + coord * 0.22);
    const patrolTurn = Math.min(1, turnRate * 0.85);

    for (let i = 0; i < n; i++) {
        ensurePatrolAnchor(hunters[i]!, i, n, canvasW, canvasH, layoutScale);
    }

    const activeFlags = selectActiveHunters(
        hunters,
        cursorX,
        cursorY,
        activateRadius,
        engageRadius,
        releaseRadius,
        maxActive,
    );

    const activeHunters: HunterBallLike[] = [];
    const activeIndices: number[] = [];
    for (let i = 0; i < n; i++) {
        if (activeFlags[i]) {
            activeHunters.push(hunters[i]!);
            activeIndices.push(i);
        }
    }

    const packTargets = computePackTargets(
        activeHunters,
        activeIndices,
        cursorX,
        cursorY,
        cursorVx,
        cursorVy,
        layoutScale,
    );

    const px =
        cursorX +
        (Math.hypot(cursorVx, cursorVy) > 0.35 * layoutScale
            ? (cursorVx / Math.hypot(cursorVx, cursorVy)) *
              Math.min(HUNTER_PREDICT_LEAD * layoutScale, Math.hypot(cursorVx, cursorVy) * 3.4)
            : 0);
    const py =
        cursorY +
        (Math.hypot(cursorVx, cursorVy) > 0.35 * layoutScale
            ? (cursorVy / Math.hypot(cursorVx, cursorVy)) *
              Math.min(HUNTER_PREDICT_LEAD * layoutScale, Math.hypot(cursorVx, cursorVy) * 3.4)
            : 0);

    for (let i = 0; i < n; i++) {
        const ball = hunters[i]!;
        const isActive = activeFlags[i]!;

        if (isActive) {
            const target = packTargets.get(i) ?? { x: px, y: py };
            const dist = Math.hypot(ball.x - px, ball.y - py);
            const localTurn = dist > ringR * 2 ? Math.min(1, turnRate * 1.35) : turnRate;
            steerToward(ball, target.x, target.y, maxSpeed, localTurn);

            const spd = Math.hypot(ball.vx, ball.vy);
            if (spd < maxSpeed * 0.12 && dist > ringR * 1.4) {
                steerToward(ball, px, py, maxSpeed, Math.min(1, localTurn * 1.6));
            }
        } else {
            const anchor = { x: ball.hunterPatrolX!, y: ball.hunterPatrolY! };
            const target = patrolTarget(ball, anchor.x, anchor.y, patrolRadius);
            steerToward(ball, target.x, target.y, patrolSpeed, patrolTurn);

            const spd = Math.hypot(ball.vx, ball.vy);
            const distAnchor = Math.hypot(ball.x - anchor.x, ball.y - anchor.y);
            if (spd < patrolSpeed * 0.1 && distAnchor > patrolRadius * 0.5) {
                steerToward(ball, anchor.x, anchor.y, patrolSpeed, Math.min(1, patrolTurn * 1.4));
            }
        }

        ball.x += ball.vx * mult;
        ball.y += ball.vy * mult;
        bounced[i] = bounceOffWalls(ball, canvasW, canvasH);
    }

    return bounced;
}

export function drawHunterBall(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    r: number,
): void {
    ctx.save();
    ctx.fillStyle = HUNTER_BALL_FILL;
    ctx.shadowColor = HUNTER_BALL_GLOW;
    ctx.shadowBlur = Math.max(8, r * 1.1);
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = Math.max(4, r * 0.5);
    ctx.fillStyle = "rgba(255,255,255,0.35)";
    ctx.beginPath();
    ctx.arc(x - r * 0.25, y - r * 0.25, r * 0.32, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
}
